import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { 
  Play, Clock, Calendar, Users, Star, Zap, Crown, Check, 
  Radio, Tv, Gift, Ticket, ChevronRight, X, Lock, Sparkles,
  Video, MessageCircle, Download, Award, Eye, Volume2, Drum, Music
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for highlights
const HighlightIconMap = {
  Drum, Users, MessageCircle, Star, Sparkles, Music, Play, Video, Award, Gift
};

// Countdown Timer Component
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(targetDate) - new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <motion.div 
        key={value}
        initial={{ scale: 1.2, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 backdrop-blur-sm border border-yellow-500/30 rounded-xl flex items-center justify-center"
      >
        <span className="text-3xl sm:text-4xl font-black text-yellow-400">{String(value).padStart(2, '0')}</span>
      </motion.div>
      <span className="text-xs text-zinc-500 mt-2 uppercase tracking-wider">{label}</span>
    </div>
  );

  return (
    <div className="flex gap-3 sm:gap-4">
      <TimeBlock value={timeLeft.days} label="Days" />
      <div className="text-3xl text-yellow-500/50 self-center mb-6">:</div>
      <TimeBlock value={timeLeft.hours} label="Hours" />
      <div className="text-3xl text-yellow-500/50 self-center mb-6">:</div>
      <TimeBlock value={timeLeft.minutes} label="Mins" />
      <div className="text-3xl text-yellow-500/50 self-center mb-6">:</div>
      <TimeBlock value={timeLeft.seconds} label="Secs" />
    </div>
  );
};

// Pricing Tier Card
const PricingTier = ({ tier, eventId, onPurchase, isPopular }) => {
  const colorClasses = {
    zinc: 'from-zinc-500/20 to-zinc-600/20 border-zinc-500/30 hover:border-zinc-400/50',
    yellow: 'from-yellow-500/20 to-amber-500/20 border-yellow-500/50 hover:border-yellow-400',
    purple: 'from-purple-500/20 to-pink-500/20 border-purple-500/50 hover:border-purple-400'
  };

  const buttonClasses = {
    zinc: 'bg-zinc-700 hover:bg-zinc-600',
    yellow: 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black',
    purple: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400'
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      className={`relative bg-gradient-to-br ${colorClasses[tier.color]} backdrop-blur-sm border-2 rounded-2xl p-6 transition-all ${isPopular ? 'ring-2 ring-yellow-500/50' : ''}`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-xs font-bold rounded-full">
            MOST POPULAR
          </span>
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-4xl font-black text-white">${tier.price}</span>
          <span className="text-zinc-500">/event</span>
        </div>
      </div>

      <ul className="space-y-3 mb-6">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-center gap-3 text-sm text-zinc-300">
            <Check className={`w-5 h-5 flex-shrink-0 ${tier.color === 'yellow' ? 'text-yellow-500' : tier.color === 'purple' ? 'text-purple-400' : 'text-zinc-400'}`} />
            {feature}
          </li>
        ))}
      </ul>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => onPurchase(tier)}
        className={`w-full py-3 rounded-xl font-bold text-white transition-all ${buttonClasses[tier.color]}`}
      >
        Get Access
      </motion.button>
    </motion.div>
  );
};

// Live Stream Embed
const LiveStreamEmbed = ({ twitchChannel, isLive, hasAccess }) => {
  if (!hasAccess) {
    return (
      <div className="aspect-video bg-zinc-900 rounded-2xl flex items-center justify-center border border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10" />
        <div className="text-center z-10">
          <Lock className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 text-lg">Purchase access to watch this event</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aspect-video bg-zinc-900 rounded-2xl overflow-hidden border border-yellow-500/20 relative">
      {isLive && (
        <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1 bg-red-500 rounded-full">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
          <span className="text-white text-sm font-bold">LIVE</span>
        </div>
      )}
      
      {twitchChannel ? (
        // PRE-DEPLOY CHECKLIST — Twitch embed:
        // Every domain that ships this iframe (preview + custom domain) must be
        // registered in Twitch's parent= allowlist for the channel to render.
        // Dashboard: https://dev.twitch.tv/console → Application → Add domain.
        // Otherwise the player refuses to load with no visible error to users.
        <iframe
          src={`https://player.twitch.tv/?channel=${twitchChannel}&parent=${window.location.hostname}`}
          frameBorder="0"
          allowFullScreen
          scrolling="no"
          className="w-full h-full"
          title="Live Stream"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <Tv className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">Stream will appear here when live</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function PayPerView() {
  const { user, isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ppv/settings`);
      const data = await response.json();
      setSettings(data);
      if (data.upcomingEvents?.length > 0) {
        setSelectedEvent(data.upcomingEvents[0]);
      }
    } catch (error) {
      console.error('Error fetching PPV settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = (tier) => {
    setSelectedTier(tier);
    setShowPurchaseModal(true);
  };

  const [guestEmail, setGuestEmail] = useState('');

  const processPurchase = async () => {
    // For guests, require email
    const customerEmail = user?.email || guestEmail;
    if (!customerEmail || !customerEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setPurchasing(true);
    try {
      // Create Stripe checkout session
      const response = await fetch(`${API_URL}/api/create-ppv-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: selectedEvent?.title || 'event',
          tierId: selectedTier?.id,
          tierName: selectedTier?.name,
          price: selectedTier?.price,
          userId: user?.id || 'guest',
          email: customerEmail
        })
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.detail || 'Failed to create checkout');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Unable to process purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading events...</p>
        </div>
      </div>
    );
  }

  const currentEvent = selectedEvent || settings?.upcomingEvents?.[0];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* ========== EPIC HERO SECTION ========== */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated background */}
        <motion.div 
          className="absolute inset-0"
          style={{ scale: heroScale }}
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${settings?.heroImage || currentEvent?.image}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />
        </motion.div>

        {/* Animated scan lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent"
              style={{ top: `${i * 5}%` }}
              animate={{ opacity: [0, 0.5, 0], x: ['-100%', '100%'] }}
              transition={{ duration: 3, delay: i * 0.1, repeat: Infinity, ease: "linear" }}
            />
          ))}
        </div>

        {/* Glowing orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/20 rounded-full blur-3xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </div>

        {/* Main content */}
        <motion.div 
          className="relative z-10 max-w-6xl mx-auto px-6 text-center"
          style={{ opacity: heroOpacity }}
        >
          {/* Live indicator or upcoming badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            {currentEvent?.isLive ? (
              <span className="inline-flex items-center gap-2 px-6 py-2 bg-red-500/20 border border-red-500/50 rounded-full">
                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-red-400 font-bold">LIVE NOW</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 px-6 py-2 bg-yellow-500/20 border border-yellow-500/50 rounded-full">
                <Radio className="w-4 h-4 text-yellow-500" />
                <span className="text-yellow-400 font-bold">UPCOMING EVENT</span>
              </span>
            )}
          </motion.div>

          {/* Main title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-5xl sm:text-7xl md:text-8xl font-black mb-4 tracking-tight"
          >
            <span className="bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 bg-clip-text text-transparent">
              {settings?.heroTitle || "LIVE PAY-PER-VIEW"}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-zinc-400 mb-8 max-w-3xl mx-auto"
          >
            {settings?.heroSubtitle}
          </motion.p>

          {/* Current event card */}
          {currentEvent && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8 max-w-2xl mx-auto mb-10"
            >
              <div className="flex items-center justify-center gap-6 mb-6 text-sm text-zinc-400">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-yellow-500" />
                  {new Date(currentEvent.date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-500" />
                  {currentEvent.time}
                </span>
                <span className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-yellow-500" />
                  {currentEvent.duration}
                </span>
              </div>

              <h2 className="text-3xl font-bold text-white mb-2">{currentEvent.title}</h2>
              <p className="text-yellow-500 mb-4">{currentEvent.subtitle}</p>

              {/* Countdown */}
              {!currentEvent.isLive && (
                <div className="flex justify-center">
                  <CountdownTimer targetDate={`${currentEvent.date}T19:00:00`} />
                </div>
              )}
            </motion.div>
          )}

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <motion.a
              href="#pricing"
              whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(245, 158, 11, 0.5)" }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-full text-black font-black text-lg"
            >
              <Ticket className="w-6 h-6" />
              Get Your Ticket
              <ChevronRight className="w-5 h-5" />
            </motion.a>
            <motion.a
              href="/ppv/watch"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-full text-white font-bold text-lg hover:border-yellow-500/50 transition-colors"
            >
              <Play className="w-5 h-5" />
              Watch Event
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
          >
            <motion.div 
              className="w-1.5 h-3 bg-yellow-500 rounded-full"
              animate={{ y: [0, 8, 0], opacity: [1, 0.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* ========== EVENT HIGHLIGHTS ========== */}
      {currentEvent?.highlights?.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-black to-zinc-950">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-yellow-500 font-bold tracking-[0.3em] text-sm mb-4 block">WHAT TO EXPECT</span>
              <h2 className="text-4xl md:text-5xl font-black text-white">
                Event <span className="text-yellow-500">Highlights</span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {currentEvent.highlights.map((highlight, index) => {
                // Support both string highlights and object highlights
                const isObject = typeof highlight === 'object';
                const title = isObject ? highlight.title : highlight;
                const description = isObject ? highlight.description : null;
                const iconName = isObject ? highlight.icon : 'Sparkles';
                const Icon = HighlightIconMap[iconName] || Sparkles;
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    onClick={() => description && setSelectedHighlight(highlight)}
                    className={`bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6 text-center transition-all ${description ? 'cursor-pointer hover:border-yellow-500/50' : ''}`}
                  >
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.2 }}
                      transition={{ duration: 0.5 }}
                      className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-500/20 to-amber-500/20 flex items-center justify-center"
                    >
                      <Icon className="w-7 h-7 text-yellow-500" />
                    </motion.div>
                    <p className="text-white font-medium mb-2">{title}</p>
                    {description && (
                      <span className="text-yellow-500 text-sm font-medium flex items-center justify-center gap-1">
                        Learn More <ChevronRight className="w-4 h-4" />
                      </span>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ========== HIGHLIGHT DETAIL MODAL ========== */}
      <AnimatePresence>
        {selectedHighlight && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={() => setSelectedHighlight(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-500/20 to-amber-500/20 flex items-center justify-center">
                    {(() => {
                      const Icon = HighlightIconMap[selectedHighlight.icon] || Sparkles;
                      return <Icon className="w-7 h-7 text-yellow-500" />;
                    })()}
                  </div>
                  <h3 className="text-xl font-bold text-white">{selectedHighlight.title}</h3>
                </div>
                <button
                  onClick={() => setSelectedHighlight(null)}
                  className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
              
              <p className="text-zinc-300 leading-relaxed">
                {selectedHighlight.description}
              </p>
              
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedHighlight(null)}
                  className="w-full py-3 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-xl font-bold text-black"
                >
                  Got It!
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========== PRICING SECTION ========== */}
      <section id="pricing" className="py-32 bg-zinc-950 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(234,179,8,0.05),transparent_60%)]" />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-yellow-500 font-bold tracking-[0.3em] text-sm mb-4 block">CHOOSE YOUR ACCESS</span>
            <h2 className="text-4xl md:text-6xl font-black text-white mb-4">
              Get Your <span className="text-yellow-500">Ticket</span>
            </h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              Select the perfect viewing experience for you
            </p>
          </motion.div>

          {/* Admin trial bypass — visible only when admin */}
          {isAdminUser && (
            <div className="mb-10 p-5 rounded-2xl border-2 border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <Crown className="w-6 h-6 text-yellow-400 shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-yellow-300 text-lg">Admin Trial Access</h3>
                  <p className="text-sm text-yellow-100/80 mt-1 max-w-xl">
                    Skip payment and jump straight into the event stream to verify the video works end-to-end. No charge.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/ppv-watch?admin=1`)}
                data-testid="admin-trial-watch-ppv-btn"
                className="px-6 py-3 rounded-xl font-bold bg-yellow-500 hover:bg-yellow-400 text-black transition-colors flex items-center gap-2 shrink-0"
              >
                <Play className="w-5 h-5" /> Watch Now (Free)
              </button>
            </div>
          )}

          {/* Pricing tiers — hidden entirely when admin (they use the Watch Now Free button above) */}
          {!isAdminUser && (
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {currentEvent?.tiers?.map((tier, index) => (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <PricingTier 
                    tier={tier} 
                    eventId={currentEvent.title}
                    onPurchase={handlePurchase}
                    isPopular={tier.popular}
                  />
                </motion.div>
              ))}
            </div>
          )}

          {/* Season Pass — hidden for admin */}
          {!isAdminUser && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-purple-500/20 via-zinc-900 to-pink-500/20 border-2 border-purple-500/30 rounded-3xl p-8 md:p-12"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Crown className="w-8 h-8 text-purple-400" />
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-sm font-bold rounded-full">BEST VALUE</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-black text-white mb-2">Season Pass</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-5xl font-black text-purple-400">${settings?.seasonPassPrice}</span>
                  <span className="text-zinc-500">/year</span>
                </div>
                <p className="text-zinc-400 mb-6">
                  Get unlimited access to ALL live events for an entire year. The ultimate experience for true fans.
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handlePurchase({ 
                    id: 'season_pass', 
                    name: 'Season Pass', 
                    price: settings?.seasonPassPrice || 99.99, 
                    color: 'purple' 
                  })}
                  className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-bold text-white"
                >
                  Get Season Pass
                </motion.button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {settings?.seasonPassFeatures?.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 text-sm">
                    <Check className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    <span className="text-zinc-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
          )}
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-yellow-500 font-bold tracking-[0.3em] text-sm mb-4 block">SIMPLE PROCESS</span>
            <h2 className="text-4xl md:text-5xl font-black text-white">
              How It <span className="text-yellow-500">Works</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Ticket, title: "Get Your Ticket", description: "Choose your preferred viewing tier and complete your purchase securely" },
              { icon: Calendar, title: "Mark Your Calendar", description: "You'll receive an email reminder before the event goes live" },
              { icon: Tv, title: "Watch & Enjoy", description: "Log in at showtime and experience the magic from anywhere in the world" }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 10 }}
                  className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-yellow-500/20 to-amber-500/20 border border-yellow-500/30 flex items-center justify-center"
                >
                  <step.icon className="w-10 h-10 text-yellow-500" />
                </motion.div>
                <div className="text-3xl font-black text-yellow-500 mb-2">{index + 1}</div>
                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-zinc-400">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PURCHASE MODAL ========== */}
      <AnimatePresence>
        {showPurchaseModal && selectedTier && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={() => setShowPurchaseModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-white">{selectedTier.name}</h3>
                  <p className="text-zinc-400">for {currentEvent?.title}</p>
                </div>
                <button
                  onClick={() => setShowPurchaseModal(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>

              <div className="bg-zinc-800/50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-zinc-400">Ticket Price</span>
                  <span className="text-2xl font-bold text-white">${selectedTier.price}</span>
                </div>
                <div className="border-t border-zinc-700 pt-4">
                  <p className="text-sm text-zinc-500 mb-2">Includes:</p>
                  <ul className="space-y-2">
                    {selectedTier.features?.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Email input for guests */}
              {!user && (
                <div className="mb-6">
                  <label className="block text-sm text-zinc-400 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none"
                  />
                  <p className="text-xs text-zinc-500 mt-2">
                    We'll send your ticket and access link to this email
                  </p>
                </div>
              )}

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={processPurchase}
                disabled={purchasing || (!user && !guestEmail)}
                className="w-full py-4 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-xl font-bold text-black flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {purchasing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Complete Purchase - ${selectedTier?.price}
                  </>
                )}
              </motion.button>

              <p className="text-center text-xs text-zinc-500 mt-4">
                Secure payment powered by Stripe • No account required
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AdminEditorLink to="/admin/dashboard#ppv-editor-btn" label="Edit PPV Events" />
    </div>
  );
}
